import moment from 'moment';
import { CountryCode } from '../../index.js';
import { enteredAmount, hasWriteOffAmounts, writtenOffAmount, } from '../case-vouchers/index.js';
import { FixedDistributionDateBehavior, } from '../fixed-distributions/index.js';
import { VoucherCategory, VoucherTypeIds, VoucherTypeSetting, } from '../vouchers/index.js';
import { isCreditNote, isPayment } from '../vouchers/matcher.js';
import { isCalculatedInterest } from '../vouchers/setup.js';
import { calcCollectionCosts as calcCollectionCostsDK } from './collection-costs/denmark.js';
import { calcCollectionCosts as calcCollectionCostsGL } from './collection-costs/greenland.js';
export const groupVouchersByParent = (vouchers) => {
    const childrenMap = {};
    for (const voucher of vouchers) {
        const parentId = voucher.referenceCaseVoucherId;
        if (parentId !== null && parentId !== undefined) {
            if (!childrenMap[parentId]) {
                childrenMap[parentId] = [];
            }
            childrenMap[parentId].push(voucher);
        }
    }
    const voucherIds = new Set(vouchers.map((v) => v.caseVoucherId));
    return vouchers
        .filter((voucher) => {
        return (voucher.referenceCaseVoucherId === null ||
            voucher.referenceCaseVoucherId === undefined ||
            !voucherIds.has(voucher.referenceCaseVoucherId));
    })
        .map((voucher) => ({
        ...voucher,
        children: childrenMap[voucher.caseVoucherId] || [],
    }));
};
export const getVoucherLabel = (caseVoucher, lang) => caseVoucher.setting === VoucherTypeSetting.TextAsLabel && caseVoucher.text
    ? caseVoucher.text
    : lang.economyVoucherTypeLabels[caseVoucher.voucherTypeId] || caseVoucher.label;
export { isPayment };
export const interruptsLimitationPeriod = (caseVoucher) => caseVoucher.setting === VoucherTypeSetting.Payment &&
    caseVoucher.settingDetails.interruptsLimitationPeriod;
export const getLimitationInterruptingPayments = (caseVouchers) => caseVouchers.filter((v) => interruptsLimitationPeriod(v) && v.amount !== 0);
// Engine 2 stores calculated interest net of write-offs, down to zero; it is
// debt even then, and takes at most what is alive and unpaid, so
// distributions kept to it may exceed its amount.
export const isWrittenDownInterest = (voucher) => hasWriteOffAmounts(voucher) && isCalculatedInterest(voucher);
export const postProcessing = (caseVouchers, filter) => {
    let newCaseVouchers = [];
    for (const voucher of caseVouchers) {
        if (!filter.includeDeletedVouchers && voucher.deletedAt != null) {
            continue;
        }
        // Engine v2 vouchers carry their own write-offs and are never dropped.
        if (hasWriteOffAmounts(voucher)) {
            newCaseVouchers.push(voucher);
            continue;
        }
        if (!filter.includeExpiredVouchers && voucher.expired) {
            continue;
        }
        if (!filter.includeIrrecoverableVouchers && voucher.irrecoverableDate) {
            continue;
        }
        if (voucher.expired || voucher.irrecoverableDate) {
            voucher.originalAmount = voucher.originalAmount || voucher.amount;
            voucher.amount = 0;
        }
        newCaseVouchers.push(voucher);
    }
    return newCaseVouchers;
};
// Engine 2 keeps written-off vouchers for its calculation. A list leaves out
// those with nothing left to show, as engine 1 drops them.
export const isListed = (voucher) => voucher.amount !== 0 || writtenOffAmount(voucher) === 0;
// Debtors don't see what engine 1 expired. Engine 2 vouchers stay: their
// write-offs are in their own columns, and the payments on them need them.
export const isVisibleToDebtor = (voucher) => !voucher.expired || hasWriteOffAmounts(voucher);
// A struck-through line no longer counts. Engine 1 drops expired and
// irrecoverable vouchers whole; engine 2 writes off parts, so only a voucher
// with nothing left to show no longer counts.
export const isStruckThrough = (voucher) => voucher.deletedAt != null ||
    (hasWriteOffAmounts(voucher)
        ? !isListed(voucher)
        : voucher.expired || voucher.irrecoverableDate != null);
// What of a voucher counts toward the balance, and what was written off, the
// same on both engines: engine 1 drops expired and irrecoverable vouchers
// whole, engine 2 writes off their unpaid part. Calculated interest has no
// amount of its own to write off from.
export const countedAmounts = (voucher) => {
    const [counted] = postProcessing([{ ...voucher }], {
        includeDeletedVouchers: true,
        includeExpiredVouchers: true,
        includeIrrecoverableVouchers: true,
    });
    return {
        amount: counted.amount,
        originalAmount: isCalculatedInterest(voucher)
            ? null
            : enteredAmount(counted),
        writtenOffAmount: hasWriteOffAmounts(voucher)
            ? writtenOffAmount(voucher)
            : Math.abs(enteredAmount(counted) - counted.amount),
    };
};
// What vouchers count toward a balance together, on either engine.
export const countedSum = (vouchers) => vouchers.reduce((total, voucher) => total + countedAmounts(voucher).amount, 0);
// Expired debt takes no payment. Engine 2 writes off only what was unpaid of
// it, so a credit note still corrects it, as if the corrected part never
// existed; engine 1 drops it whole.
export const correctsExpired = (credit, expired, dateBehavior) => isCreditNote(credit) &&
    hasWriteOffAmounts(expired) &&
    dateBehavior === FixedDistributionDateBehavior.FromOriginal;
// A payment is countered by its own type, so the counter entry has the same
// limitation-period behaviour as the original; debt by a credit note.
export const counterEntryVoucherTypeId = (original) => isPayment(original)
    ? original.voucherTypeId
    : original.category === VoucherCategory.TRANSACTION
        ? VoucherTypeIds.DEPOSIT
        : VoucherTypeIds.CREDITNOTE;
export const isInsideTagInterval = (tag, date) => {
    const _date = moment(date);
    return (tag &&
        (!tag.from || moment(tag.from).isBefore(_date)) &&
        (!tag.to || moment(tag.to).isAfter(_date)));
};
export const calcCollectionCost = async (caseVouchers, documents, economyPolicy, fixedDistributions, selfCollection, conditions, country) => {
    switch (country) {
        case CountryCode.GL: {
            return await calcCollectionCostsGL(caseVouchers, documents, economyPolicy, fixedDistributions, selfCollection, conditions);
        }
        case CountryCode.DK: {
            return await calcCollectionCostsDK(caseVouchers, documents, economyPolicy, fixedDistributions, selfCollection, conditions);
        }
        default: {
            return await calcCollectionCostsDK(caseVouchers, documents, economyPolicy, fixedDistributions, selfCollection, conditions);
        }
    }
};
//# sourceMappingURL=vouchers.js.map